import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock, Loader2, Send, Sparkles, Trash2, Wand2 } from "lucide-react";
import { api, apiError, logError, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const TONES = [
  ["polite", "Polite"],
  ["firm", "Firm"],
  ["urgent", "Urgent"],
];
const PRIORITIES = ["Normal", "High", "Urgent"];

const statusTone = (s) =>
  s === "Done" ? "text-emerald-600" : s === "Accepted" ? "text-amber-600" : "text-primary";

function DelegateDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [team, setTeam] = useState([]);
  const [busy, setBusy] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [form, setForm] = useState({
    title: "", note: "", tone: "polite", priority: "Normal", due_date: "", message: "",
  });
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/team").then(({ data }) => setTeam((data.members || []).filter((m) => m.is_active)))
      .catch((e) => logError("team", e));
  }, [open]);

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const polish = async () => {
    setPolishing(true);
    try {
      const { data } = await api.post("/tasks/tone-preview", { note: form.note, tone: form.tone });
      setForm((f) => ({ ...f, message: data.message }));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setPolishing(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/tasks", { ...form, assignee_ids: picked });
      toast.success("Work sent — it will pop up on their screen");
      setOpen(false);
      setForm({ title: "", note: "", tone: "polite", priority: "Normal", due_date: "", message: "" });
      setPicked([]);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="delegate-work-button" className="gap-2">
          <Send className="h-4 w-4" /> Delegate work
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="delegate-dialog">
        <DialogHeader>
          <DialogTitle>Delegate work</DialogTitle>
          <DialogDescription>
            Write it in one line — AI shapes the tone before it reaches them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            data-testid="task-title-input"
            placeholder="Short title (e.g. Load slide panels for Surat)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <Textarea
            data-testid="task-note-input"
            placeholder="What needs to be done, by when…"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
          />
          <div className="grid gap-2 sm:grid-cols-3">
            <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
              <SelectTrigger data-testid="task-tone-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TONES.map(([v, l]) => (
                  <SelectItem key={v} value={v} data-testid={`tone-${v}`}>{l} tone</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              data-testid="task-due-input"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
            />
          </div>

          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="task-polish-button"
            disabled={form.note.trim().length < 5 || polishing}
            onClick={polish}
          >
            {polishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            Set the tone with AI
          </Button>

          {form.message && (
            <Textarea
              data-testid="task-message-preview"
              className="bg-secondary"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
            />
          )}

          <div>
            <div className="overline mb-1">Send to</div>
            <div className="flex flex-wrap gap-1.5" data-testid="task-assignee-list">
              {team.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`assignee-${m.id}`}
                  onClick={() => toggle(m.id)}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                    picked.includes(m.id)
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border hover:bg-secondary"
                  }`}
                >
                  {m.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            data-testid="task-send-button"
            onClick={submit}
            disabled={busy || !picked.length || form.title.trim().length < 2}
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send work
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskCard({ task, mine, onChange }) {
  const [reply, setReply] = useState("");

  const act = async (path, body) => {
    try {
      await api.post(`/tasks/${task.id}/${path}`, body || {});
      onChange();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="border-b border-border py-3" data-testid={`task-card-${task.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{task.title}</span>
            <Badge variant="outline" className="text-[10px] uppercase">{task.tone}</Badge>
            {task.priority !== "Normal" && (
              <Badge className="text-[10px]">{task.priority}</Badge>
            )}
            <span className={`text-xs ${statusTone(task.status)}`} data-testid={`task-status-${task.id}`}>
              {task.status}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{task.message}</p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {mine ? `From ${task.assigned_by_name}` : `To ${task.assigned_to_name}`}
            {task.due_date ? ` • due ${shortDate(task.due_date)}` : ""}
            {task.reply ? ` • reply: “${task.reply}”` : ""}
          </p>
        </div>
        {!mine && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            data-testid={`task-cancel-${task.id}`}
            onClick={async () => {
              try {
                await api.delete(`/tasks/${task.id}`);
                onChange();
              } catch (e) {
                toast.error(apiError(e));
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        )}
      </div>

      {mine && task.status !== "Done" && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {task.status === "New" && (
            <Button size="sm" variant="outline" data-testid={`task-accept-${task.id}`}
              onClick={() => act("accept")}>
              Accept
            </Button>
          )}
          <Button size="sm" className="gap-1.5" data-testid={`task-done-${task.id}`}
            onClick={() => act("done", { reply })}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark done
          </Button>
          <Input
            className="h-8 max-w-xs"
            placeholder="Short reply (optional)"
            data-testid={`task-reply-input-${task.id}`}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <Button size="sm" variant="ghost" data-testid={`task-reply-${task.id}`}
            disabled={!reply.trim()} onClick={() => act("reply", { reply })}>
            Send reply
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Work() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const canDelegate = Boolean(user?.role && user.role !== "employee" && user.role !== "sales_executive");

  const load = useCallback(() => {
    api.get("/tasks?scope=mine").then(({ data }) => setMine(data))
      .catch((e) => logError("my tasks", e));
    if (canDelegate)
      api.get("/tasks?scope=assigned").then(({ data }) => setAssigned(data))
        .catch((e) => logError("assigned tasks", e));
  }, [canDelegate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        testId="work-header"
        title="Work"
        subtitle="Work handed to you, and the work you have handed out."
        actions={canDelegate ? <DelegateDialog onDone={load} /> : null}
      />

      <Tabs defaultValue="mine" className="p-4">
        <TabsList>
          <TabsTrigger value="mine" data-testid="work-tab-mine">
            <Clock className="mr-1.5 h-3.5 w-3.5" /> My work ({mine.filter((t) => t.status !== "Done").length})
          </TabsTrigger>
          {canDelegate && (
            <TabsTrigger value="assigned" data-testid="work-tab-assigned">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Delegated ({assigned.length})
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="mine" data-testid="my-work-list">
          {mine.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Nothing on your plate right now.
            </p>
          ) : (
            mine.map((t) => <TaskCard key={t.id} task={t} mine onChange={load} />)
          )}
        </TabsContent>

        {canDelegate && (
          <TabsContent value="assigned" data-testid="delegated-work-list">
            {assigned.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                You have not delegated any work yet.
              </p>
            ) : (
              assigned.map((t) => <TaskCard key={t.id} task={t} onChange={load} />)
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
